import {
  ArgumentError,
  InvalidInputError,
  UnauthorizedError,
  UnknownApiError,
} from './errors';
import {
  assertAuthData,
  assertString,
  assertRequiredString,
  assertRequiredStringArray,
} from './utils';

export default function (api) {
  /**
   * @typedef {Object} Channel
   * @property {number} id
   * @property {number} listOrder
   * @property {string} name
   * @property {string[]} tags
   */

  /**
   * @typedef {Object} Product
   * @property {string} id
   * @property {string} name
   * @property {string} tagline
   * @property {string} description
   * @property {string} image
   * @property {number} price
   * @property {Channel[]} channels
   * @property {string[]} channelIds
   * @property {string} requiredPack
   * @property {number} personalisationIncentive
   * @property {string} replaceProduct
   */

  /**
   * @memberof API
   * @instance
   */
  const subscription = {
    /**
     * @typedef {Object} Discount
     */

    /**
     * @typedef {Object} Campaign
     */

    /**
     * @typedef {Object} OverlapWarning
     */

    /**
     * @typedef {Object} TrialInfo
     * @property {boolean} isTrial
     * @property {number} trialEndDate - Unix timestamp in 13-digit
     */

    /**
     * @typedef {Object} UserSubscription
     * @property {Discount[]} discounts
     * @property {Product[]} packs
     * @property {Campaign[]} campaigns
     * @property {number} nextBillingDate - Unix timestamp in 13-digit
     * @property {number} lastBillingDate - Unix timestamp in 13-digit
     * @property {TrialInfo} trailInfo
     * @property {number} monthlyGrossPrice
     * @property {number} monthlyNetPrice
     * @property {string} currency
     * @property {number} readTimestamp - Unix timestamp in 13-digit
     * @property {boolean} freeRider
     * @property {string} externalBilling
     * @property {string} status
     *   `ACTIVE_SUBSCRIPTION`, `EXPIRING_SUBSCRIPTION` or `NO_SUBSCRIPTION`.
     */

    /**
     * Get current user's subscription.
     *
     * @method
     *
     * @return {Promise}
     * @fulfill {UserSubscription}
     * @reject {AuthTokenNotSetError}
     * @reject {UnknownApiError}
     */
    async get() {
      assertAuthData(api.authData);

      const url = `/superscription/v2/users/${api.authData.userId}/subscription`;
      const res = await api.request('get', url);

      switch (res.status) {
        case 200: {
          return res.body;
        }
        default: {
          throw new UnknownApiError(undefined, res);
        }
      }
    },

    /**
     * Cancel packages in the current user's subscription.
     *
     * @method
     *
     * @param {string[]} payload.productIds
     *
     * @return {Promise}
     * @fulfill {undefined}
     * @reject {AuthTokenNotSetError}
     * @reject {ArgumentError}
     * @reject {UnauthorizedError}
     * @reject {UnknownApiError}
     */
    async cancel(payload = {}) {
      assertAuthData(api.authData);
      assertRequiredStringArray(payload.productIds, 'payload.productIds');

      const url = `/superscription/v2/users/${api.authData.userId}/subscription/cancel`;
      const data = {
        productIds: payload.productIds,
        source: 'user',
      };

      const res = await api.request('put', url, { data });

      switch (res.status) {
        case 204: {
          return undefined;
        }
        case 400: {
          throw new ArgumentError('Bad request', res);
        }
        case 401: {
          throw new UnauthorizedError('Authorization failed', res);
        }
        default: {
          throw new UnknownApiError(undefined, res);
        }
      }
    },

    /**
     * @typedef PriceResponse
     * @property {number} grossPrice
     * @property {number} netPrice
     * @property {number} monthlyGrossPrice
     * @property {number} monthlyNetPrice
     * @property {number} monthlyGrossPriceChange
     * @property {number} monthlyNetPriceChange
     * @property {Discount[]} discounts
     * @property {string} currency
     * @property {number} fromDate - Unix timestamp in 13-digit
     * @property {number} toDate - Unix timestamp in 13-digit
     * @property {Object} trial
     * @property {Product[]} packs
     * @property {Campaign[]} campaigns
     * @property {OverlapWarning[]} overlapWarnings
     */

    /**
     * Get price of given products.
     *
     * @method
     *
     * @param {Object} payload
     * @param {string} payload.market
     * @param {string[]} payload.productIds
     * @param {string=} [payload.userId]
     * @param {string=} [payload.promoCode]
     *
     * @return {Promise}
     * @fulfill {PriceResponse}
     * @reject {ArgumentError}
     * @reject {InvalidInputError}
     * @reject {UnknownApiError}
     */
    async price(payload = {}) {
      assertRequiredString(payload.market, 'payload.market');
      assertRequiredStringArray(payload.productIds, 'payload.productIds');
      assertString(payload.userId, 'payload.userId');
      assertString(payload.promoCode, 'payload.promoCode');

      const {
        market,
        productIds,
        userId,
        promoCode,
      } = payload;

      const query = {
        productIds: productIds.join(','),
      };

      if (userId) {
        query.userId = userId;
      }

      if (promoCode) {
        query.promoCode = promoCode;
      }

      const url = `/superscription/v2/market/${market}/price`;
      const res = await api.request('get', url, { query });

      switch (res.status) {
        case 200: {
          return res.body;
        }
        case 400: {
          throw new InvalidInputError(res.body.message);
        }
        default: {
          throw new UnknownApiError('server returned with unhandled status code', res);
        }
      }
    },
  };

  return subscription;
}
